import { Grid, Table, TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import { ImportExport, MoreHoriz } from '@mui/icons-material';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CustomFieldType, getTableCell, TableHeaderCell, TableHeaderContent } from '../../common/DxTable/DxTableCommon';
import { ImageIcon } from '../../utils/UtilsComponent';

import AddIcon from '../../assets/icons/add-circle.svg';
import DeleteIcon from '../../assets/icons/delete-icon.svg';
import EditIcon from '../../assets/icons/edit-icon.svg';
import LocationIcon from '../../assets/icons/location-icon.svg';
import CalendarIcon from '../../assets/icons/calendar-icon.svg';
import SearchIcon from '../../assets/icons/search-icon.svg';
import { Input } from '../../common';
import Button from '../../common/button/Button';
import Select from '../../common/Select/Select';

import ExportIcon from '../../assets/icons/export-red-icon.svg';
import useModalConfirm from '../../hooks/useModalConfirm';
import { IUser } from '../../services/auth.service';

import { defaultValuesCamera, mappingStatusCameraBoxColor, mappingStatusCameraBox } from './constants';
import { selectCameras } from '../../state/modules/camera/cameraReducer';
import { useDeleteCameraMutation } from '../../services/cameras.service';
import { useAuth } from '../../hooks/useAuth';
import ModalAddCameraBox from './ModalAddCameraBox';
import ModalEditCameraBox from './ModalEditCameraBox';
import { useQueryParams, StringParam } from 'use-query-params';
import { ROUTE_CONTROL } from 'utils/routesMap';
import { useNavigate } from 'react-router-dom';
import { getMinutesDiffNow } from 'utils/UtilsFunctions';
import { BASE_URL } from 'services/http.service';
import * as XLSX from 'xlsx';

interface FiltersFormValue {
  search: string;
  state: string;
}

const ActionCellContent = ({
  cellProps,
  onActionClick,
}: {
  cellProps: Table.DataCellProps;
  onActionClick: (type: string, id: string | IUser) => void;
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const rowId = useMemo(() => cellProps.row?.id, [cellProps]);

  return (
    <div>
      <IconButton
        id="demo-positioned-button"
        aria-controls={open ? 'demo-positioned-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <MoreHoriz />
      </IconButton>
      <Menu
        id="demo-positioned-menu"
        aria-labelledby="demo-positioned-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => onActionClick('view', rowId)} sx={{ padding: '16px' }}>
          <ListItemIcon>
            <ImageIcon image={LocationIcon} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { fontSize: '14px' } }}>Tới vị trí trên bản đồ</ListItemText>
        </MenuItem>
        <Divider sx={{ margin: '0 16px !important' }} />

        <MenuItem sx={{ padding: '16px' }}>
          <ListItemIcon>
            <ImageIcon image={CalendarIcon} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { fontSize: '14px' } }}>Gia hạn thêm</ListItemText>
        </MenuItem>
        <Divider sx={{ margin: '0 16px !important' }} />
        <MenuItem onClick={() => onActionClick('edit', cellProps.row)} sx={{ padding: '16px' }}>
          <ListItemIcon>
            <ImageIcon image={EditIcon} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { fontSize: '14px' } }}>Chỉnh sửa thông tin</ListItemText>
        </MenuItem>
        <Divider sx={{ margin: '0 16px !important' }} />
        <MenuItem onClick={() => onActionClick('delete', rowId)} sx={{ padding: '16px' }}>
          <ListItemIcon>
            <ImageIcon image={DeleteIcon} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { fontSize: '14px', color: '#E5401C' } }}>
            Xoá camera box
          </ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};
const CameraActiveStatus = [
  {
    value: 'online',
    label: 'Đang kết nối',
  },
  {
    value: 'offline',
    label: 'Mất kết nối',
  },
];

const CamerasTable = ({ setPaginate, refetch }: { setPaginate: (p: any) => void; refetch: () => void }) => {
  const { showModalConfirm, hideModalConfirm } = useModalConfirm();
  const [query, setQuery] = useQueryParams({
    serial: StringParam,
    state: StringParam,
  });
  const defaultFiltersFormValue = {
    state: query.state || 'all',
  };
  const [searchValue, setSearchValue] = React.useState(query.serial || '');
  const [filtersFormValue, setFiltersFormValue] = React.useState(defaultFiltersFormValue);

  const [modalEditCamera, setModalEditCamera] = useState({
    show: false,
    initialValues: defaultValuesCamera,
  });

  const [showModalAdd, setShowModalAdd] = useState(false);

  const cameras = useSelector(selectCameras);
  const [deleteCamera] = useDeleteCameraMutation();
  const navigate = useNavigate();

  const {
    auth: { currentUser, currentAgency },
  } = useAuth();

  const [columns] = useState([
    { name: 'serial', title: 'Mã thiết bị' },
    { name: 'name', title: 'Tên box' },
    { name: 'location_name', title: 'Tên vị trí' },
    { name: 'ip', title: 'IP' },
    { name: 'version', title: 'Phiên bản' },
    { name: 'status', title: 'Trạng thái kích hoạt' },
    { name: 'state', title: 'Trạng thái hoạt động' },
    { name: 'action', title: 'Hành động' },
  ]);

  const [tableColumnExtensions] = useState<Table.ColumnExtension[]>([
    { columnName: 'serial', width: 150 },
    { columnName: 'name', width: 250 },
    { columnName: 'location_name', width: 400 },
    { columnName: 'ip', width: 120 },
    { columnName: 'version', width: 100 },
    { columnName: 'status', width: 220 },
    { columnName: 'state', width: 220 },
    { columnName: 'action', width: 220, align: 'center' },
  ]);

  const customField: CustomFieldType = {
    serial: {
      renderContent: ({ row }) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '14px' }}>{row?.serial}</Typography>
          </Box>
        );
      },
    },
    status: {
      renderContent: ({ row }) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '14px', color: `${mappingStatusCameraBoxColor[row.status]}` }}>
              {mappingStatusCameraBox[row.status]}
            </Typography>
          </Box>
        );
      },
    },
    state: {
      renderContent: ({ row }) => {
        const timeStamp = row ? row?.state?.timestamp : '';
        const status = row?.status;
        if (String(status) === '1') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '14px' }}>--</Typography>
            </Box>
          );
        }

        const activeMode =
          timeStamp && getMinutesDiffNow(timeStamp) < 5.05 && String(status) === '2' ? 'Online' : 'Mất kết nối';

        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '14px' }}>{activeMode}</Typography>
          </Box>
        );
      },
    },
    location_name: {
      renderContent: ({ row }) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={row?.location_name || '--'} arrow>
              <Typography sx={{ fontSize: '14px' }}>{row?.location_name || '--'}</Typography>
            </Tooltip>
          </Box>
        );
      },
    },
  };

  const goToMap = (id: string) => {
    const cbx = cameras.find((item) => item.id === id);
    if (!cbx || !cbx.location_id) return;
    navigate(`${ROUTE_CONTROL}?tab=1&locationId=${cbx.location_id}`);
  };

  const handleDeleteCamera = (id: string) => {
    if (currentUser) {
      deleteCamera({ ids: [id], agencyId: currentUser.sub_id }).then(() => {
        hideModalConfirm();
        setPaginate({});
      });
    }
  };

  const handleClick = (type: string, row: string | any) => {
    if (type === 'delete') {
      showModalConfirm({
        type: 'warning',
        title: 'Xoá camera box',
        content: 'Bạn có chắc chắn muốn xoá camera box này không?',
        confirm: {
          action: () => handleDeleteCamera(row),
          text: 'Xoá camera box',
        },
        cancel: {
          action: hideModalConfirm,
        },
      });
    }

    if (type === 'edit') {
      setModalEditCamera({
        show: true,
        initialValues: { ...row, status: row.status ? 1 : 0 },
      });
    }

    if (type === 'view') {
      goToMap(row);
      return;
    }
  };

  const handleSearch = () => {
    setQuery({
      serial: searchValue,
      state: filtersFormValue.state !== 'all' ? filtersFormValue.state : undefined,
    });
  };

  const handleFilter = () => {
    setQuery({
      serial: searchValue || undefined,
      state: filtersFormValue.state !== 'all' ? filtersFormValue.state : undefined,
    });
  };

  const handleClearFilter = () => {
    setFiltersFormValue({ state: 'all' });
    setSearchValue('');
    setQuery({
      serial: undefined,
      state: undefined,
    });
  };

  const onSearchChange = (e: any) => {
    const value = e.target.value;
    setSearchValue(value);
  };

  const handleChange = (filterName: 'state', value: any) => {
    setFiltersFormValue({
      ...filtersFormValue,
      [filterName]: value,
    } as FiltersFormValue);
  };

  const handleExport = async () => {
    const token = localStorage.getItem('access_token');
    if (!currentUser || !token) return;
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${JSON.parse(token)}`);
    const URL_CONTENT =
      currentUser.type === 'organization' ? `cameraboxs/export` : `agencies/${currentUser.sub_id}/cameraboxs/export`;
    const exportQuery = new URLSearchParams();

    if (query.serial) exportQuery.append('serial', query.serial);
    if (query.state && query.state !== 'all') exportQuery.append('state', query.state);

    const exportUrl = `${BASE_URL}/${URL_CONTENT}${exportQuery.toString() ? `?${exportQuery.toString()}` : ''}`;

    try {
      const response = await fetch(exportUrl, {
        method: 'GET',
        headers: myHeaders,
      });

      if (!response.ok) throw new Error('Failed to fetch the CSV file');
      const csvText = await response.text();
      const workbook = XLSX.read(csvText, { type: 'string' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      if (worksheet['!ref']) {
        const range = XLSX.utils.decode_range(worksheet['!ref']);

        for (let row = range.s.r + 1; row <= range.e.r; row += 1) {
          const cell = worksheet[`I${row + 1}`];
          if (!cell || cell.v === undefined || cell.v === null || cell.v === '') continue;

          const serialValue = Number(cell.v);
          if (Number.isNaN(serialValue)) continue;

          cell.t = 'n';
          cell.v = serialValue;
          cell.z = 'dd/mm/yyyy';
        }
      }

      XLSX.writeFile(workbook, 'camera_boxes.xlsx');
    } catch (error) {
      console.log(error);
    }
  };

  const dataTable = cameras.map((item, index) => ({ ...item, number: index + 1 }));

  return (
    <>
      <ModalAddCameraBox
        show={showModalAdd}
        onClose={() => {
          setShowModalAdd(false);
        }}
        onSuccess={refetch}
      />
      <ModalEditCameraBox
        {...modalEditCamera}
        onClose={() => setModalEditCamera({ ...modalEditCamera, show: false })}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'end',
            justifyContent: 'space-between',
            '& > *': {
              marginRight: '16px',
            },
          }}
        >
          <Input
            noMarginTop
            style={{ width: 311, background: '#FFFFFF' }}
            placeholder="Tìm kiếm serial"
            iconStartAdorment={<ImageIcon image={SearchIcon} />}
            onChange={onSearchChange}
            value={searchValue}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') {
                handleSearch();
                ev.preventDefault();
              }
            }}
          />
          <Select
            noMarginTop
            fullWidth
            data={CameraActiveStatus}
            selected={filtersFormValue.state}
            setSelected={(data) => handleChange('state', data)}
            style={{ width: '200px', marginRight: '16px' }}
            topLable="Trạng thái hoạt động"
            placeholder="Tất cả trạng thái"
          />
          <Button
            onClick={handleFilter}
            color="primary"
            variant="contained"
            style={{
              height: '40px',
              padding: '0 16px',
              marginRight: '16px',
            }}
          >
            Lọc
          </Button>
          <Button
            onClick={handleClearFilter}
            color="primary"
            variant="outlined"
            style={{ height: '40px', padding: '0 16px' }}
          >
            Xoá bộ lọc
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }} pt={3.5}>
          {currentAgency?.level === 0 && currentUser?.type !== 'organization' && (
            <Button style={{ marginLeft: 16 }} variant="contained" onClick={() => setShowModalAdd(true)}>
              <ImageIcon image={AddIcon} />
              <Typography sx={{ marginLeft: '8px', fontWeight: '700', fontSize: '14px' }}>Thêm camera box</Typography>
            </Button>
          )}
                    <Button variant="contained" onClick={() => handleExport()} startIcon={<ImportExport />}>
                      <Box sx={{ marginLeft: '8px' }}>Xuất Excel</Box>
                    </Button>
        </Box>
      </Box>
      <Paper sx={{ boxShadow: 'none' }}>
        <Grid rows={dataTable} columns={columns}>
          <Table
            columnExtensions={tableColumnExtensions}
            cellComponent={(props) =>
              getTableCell(props, <ActionCellContent cellProps={props} onActionClick={handleClick} />, customField)
            }
          />
          <TableHeaderRow cellComponent={TableHeaderCell} contentComponent={TableHeaderContent} />
        </Grid>
      </Paper>
    </>
  );
};

export default CamerasTable;
